import { Action } from 'widgets-for-react'
import { Map, Set } from 'immutable'
import { validateField } from './newsletterFormBlockValidation'

export interface NewsletterFormBlockState {
  data: FormState
  visited: Set<Field>
  validationMessages: Map<Field, string>
  status: 'initial' | 'submitting' | 'succeeded' | 'failed'
  errorMessage: string
}

export const genderValues = ['F', 'M'] as const
type GenderValue = typeof genderValues[number]

export const genderLabels: { [n in GenderValue]: string } = {
  F: 'Vrouw',
  M: 'Man',
}

export const newslettersId = [
  3, 119, 12, 5, 126, 100, 134
] as const

export type NewsletterId = typeof newslettersId[number];

export const newsletterName: { [n in NewsletterId]: string } = {
  3: 'Nieuwsbrief',
  119: 'Junior Nieuws',
  12: 'Nieuwsbrief BoekStart in de Kinderopvang',
  5: 'Nieuwsbrief Basisonderwijs',
  126: 'Nieuwsbrief Voortgezet Onderwijs',
  100: 'Kennemerwaard College',
  134: 'Nieuws vanuit Participatie & Zelfredzaamheid'
}
export const newsletterLabel: { [n in NewsletterId]: string } = {
  3: 'Blijf maandelijks op de hoogte van jouw bieb, over activiteiten en andere wetenswaardigheden.',
  119: 'Om de maand lees en doe tips voor jeugd en jongeren in je mailbox. Haal meer uit de Bibliotheek voor jouw gezin!',
  12: 'Uitnodigingen voor bijeenkomsten, tips en nieuws voor professionals in de voorschoolse educatie.',
  5: 'Tips en nieuws voor professionals in het primair onderwijs.',
  126: 'Tips en nieuws voor professionals in het voortgezet onderwijs.',
  100: 'Blijf op de hoogte van trainingen en bijeenkomsten voor professionals in het onderwijs.',
  134: 'Ontvang één keer per kwartaal het laatste nieuws op het gebied van Participatie & Zelfredzaamheid vanuit de bibliotheek.'
}

export interface FormState {
  emailAddress: string
	firstName: string
  lastName: string
  gender: GenderValue
  newsletters: Set<NewsletterId>
}

export type Field = keyof FormState

export const initialFormState: FormState = {
  emailAddress: '',
  firstName: '',
  lastName: '',
  gender: undefined,
  newsletters: Set<NewsletterId>()
}

export const allFields = Object.keys(initialFormState) as Array<Field>

export const initialState: NewsletterFormBlockState = {
  data: initialFormState,
  visited: Set<Field>(),
  validationMessages: Map<Field, string>(),
  status: 'initial',
  errorMessage: ""
}

export const setFieldOnState =
  (setState: (action: Action<NewsletterFormBlockState>) => void) =>
    <Field extends keyof FormState>(field: Field) =>
      (value: FormState[Field]) =>
        setState((s) => ({
          ...s, data: { ...s.data, [field]: value }
        }))

export const toggleNewsletterOnState =
  (setState: (action: Action<NewsletterFormBlockState>) => void) =>
    (value: NewsletterId) =>
      setState((s) => ({
        ...s, data: {
          ...s.data,
          newsletters: s.data.newsletters.has(value)
            ? s.data.newsletters.remove(value)
            : s.data.newsletters.add(value)
        }
      }))

export const setVisited =
  (setState: (action: Action<NewsletterFormBlockState>) => void) => (field: Field) =>
    setState((s) => ({ ...s, visited: s.visited.add(field) }))

export const validateFormData =
  (setState: (action: Action<NewsletterFormBlockState>) => void) =>
    (all?: boolean) => setState((prevState) => {
      const fields = all ? allFields : prevState.visited.toArray()

      const validationMessages = validateFields(fields, prevState.data)

      // When "all fields" is triggered once, ensure all is validated on updates
      const visited = Set(fields)

      return { ...prevState, validationMessages, visited }
    })

export const validateFields = (fields: Field[], data: FormState) => fields.reduce<Map<Field, string>>((messages, field) => {
  const result = validateField(field)(data[field])
  return result.status == 'invalid'
    ? messages.set(field, result.message)
    : messages
}, Map<Field, string>())


